import { useEffect, useState } from "react";
import { Menu, X, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const NAV = [
  { id: "home", label: "Home" },
  { id: "about", label: "About" },
  { id: "expertise", label: "Expertise" },
  { id: "achievement", label: "Achievement" },
  { id: "blog", label: "Blog" },
  { id: "freelance", label: "Freelance" },
  { id: "contact", label: "Contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("home");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const sections = NAV.map((n) => document.getElementById(n.id)).filter(Boolean);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const handleNav = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "py-3 bg-[#060a1a]/80 backdrop-blur-xl border-b border-white/[0.06]"
          : "py-5 bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between">
        <button
          data-testid="logo-button"
          onClick={() => handleNav("home")}
          className="flex items-center gap-2.5 group"
        >
          <span className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 grid place-items-center shadow-[0_0_20px_rgba(37,99,235,0.4)]">
            <Sparkles className="w-4 h-4 text-white" strokeWidth={2} />
            <span className="absolute inset-0 rounded-xl ring-1 ring-white/20" />
          </span>
          <div className="leading-tight text-left">
            <div className="font-display text-white font-semibold text-[15px] tracking-tight" style={{ fontFamily: "Outfit" }}>
              Joneten Saputra
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-blue-400/80">
              Product · Solution
            </div>
          </div>
        </button>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1 glass-strong rounded-full px-2 py-1.5">
          {NAV.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-${item.id}`}
              onClick={() => handleNav(item.id)}
              className={`relative px-4 py-2 text-sm rounded-full transition-all ${
                active === item.id
                  ? "text-white bg-white/[0.06]"
                  : "text-slate-300 hover:text-white"
              }`}
            >
              {item.label}
              {active === item.id && (
                <motion.span
                  layoutId="nav-active"
                  className="absolute inset-0 rounded-full ring-1 ring-blue-400/40 bg-blue-500/[0.08]"
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
              )}
            </button>
          ))}
        </nav>

        <div className="hidden lg:block">
          <button
            data-testid="nav-cta"
            onClick={() => handleNav("contact")}
            className="px-5 py-2.5 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-sm font-medium hover:shadow-[0_0_28px_rgba(59,130,246,0.45)] transition-shadow duration-300"
          >
            Konsultasi Project
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 text-white"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="lg:hidden mx-6 mt-3 rounded-2xl glass-strong p-4"
          >
            <div className="flex flex-col gap-1">
              {NAV.map((item) => (
                <button
                  key={item.id}
                  data-testid={`mobile-nav-${item.id}`}
                  onClick={() => handleNav(item.id)}
                  className={`text-left px-4 py-3 rounded-xl text-sm ${
                    active === item.id
                      ? "bg-blue-500/10 text-white"
                      : "text-slate-300 hover:bg-white/5"
                  }`}
                >
                  {item.label}
                </button>
              ))}
              <button
                data-testid="mobile-nav-cta"
                onClick={() => handleNav("contact")}
                className="mt-2 px-4 py-3 rounded-xl bg-blue-500 text-white text-sm font-medium"
              >
                Konsultasi Project
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
